"use client";
import React from "react";
import { AbsoluteFill, Sequence, useCurrentFrame, useVideoConfig, interpolate } from "remotion";
import { loadFont } from "@remotion/google-fonts/NotoSansDevanagari";
import { HOLES_DATA_URI } from "./trimbakHolesData";
import { DevaKathaLS2 } from "./DevaKathaLS2";
import { DevaKathaLS5 } from "./DevaKathaLS5";

const { fontFamily } = loadFont("normal", { weights: ["400", "700", "800", "900"], subsets: ["devanagari", "latin"] });

// Mahakaleshwar phrase overlays — Trimbakeshwar holes treatment. Top-left, yellow-prominent,
// per-word colour + line breaks, black drop-shadow, scattered-holes mask, word-by-word drop-in.
// Times ABSOLUTE seconds on the assembled narration (no opener shift). 1280×704 base.
const PAD = 25;
const YELLOW = "#FFD60A";
const WHITE = "#FFFFFF";
const RED = "#FF3B2E";
const SH = "0 5px 14px rgba(0,0,0,0.9), 0 2px 5px rgba(0,0,0,0.95)";
const HOLES = HOLES_DATA_URI;

const BASE_FS = 66, BIG_FS = 92, SMALL_FS = 46;
const STAGGER = 0.16, DROP = 26, DUR = 0.32;
const LH = 1.18;
const LINE_GAP = -10;

type Word = { t: string; c?: "y" | "w" | "r"; big?: boolean; small?: boolean };
export type Phrase = { lines: Word[][]; start: number; out: number; lineGap?: number; lh?: number; ls?: string };
const col = (c?: string) => (c === "w" ? WHITE : c === "r" ? RED : YELLOW);

// 46 Yash-approved phrases, each anchored to the EXACT spoken word in word_timestamps_hindi.
export const MAHA_PHRASES: Phrase[] = [
  { start: 8.99,   out: 12.4,   lines: [[{ t: "सरकार", c: "y" }, { t: "गिर", c: "r", big: true }, { t: "गई", c: "r" }]] },
  { start: 38.25,  out: 41.65,  lines: [[{ t: "चिता", c: "r" }, { t: "की", small: true }, { t: "भस्म", c: "w", big: true }]] },
  { start: 44.64,  out: 47.9,   lines: [[{ t: "महाकाल", c: "y", big: true }]] },
  { start: 50.9,   out: 53.0,   lines: [[{ t: "समय", c: "y" }, { t: "को", small: true }, { t: "निगल", c: "w", big: true }], [{ t: "जाता", c: "y" }, { t: "है", c: "y" }]] },
  { start: 53.12,  out: 56.5,   lines: [[{ t: "मृत्यु", c: "r", big: true }], [{ t: "हाथ", c: "y" }, { t: "जोड़े", c: "w" }]] },
  { start: 58.86,  out: 62.26,  lines: [[{ t: "दक्षिण", c: "y" }, { t: "दिशा", c: "w", big: true }]] },
  { start: 62.92,  out: 66.3,   lines: [[{ t: "यमराज", c: "r" }, { t: "का", small: true }, { t: "घर", c: "w", big: true }]] },
  { start: 77.98,  out: 81.4,   lines: [[{ t: "राजा", c: "y" }, { t: "स्वयं", c: "w" }, { t: "महाकाल", c: "y", big: true }]] },
  { start: 87.51,  out: 90.9,   lines: [[{ t: "सिर्फ", c: "y" }, { t: "24", c: "w", big: true }, { t: "घंटे", c: "w" }]] },
  { start: 96.9,   out: 100.3,  lines: [[{ t: "समय", c: "y" }, { t: "का", small: true }, { t: "केंद्र", c: "w", big: true }]] },
  { start: 133.69, out: 137.1,  lines: [[{ t: "शिव", c: "y" }, { t: "पुराण", c: "w", big: true }]] },
  { start: 156.33, out: 159.7,  lines: [[{ t: "दैत्य", c: "r" }, { t: "दूषण", c: "w", big: true }]] },
  { start: 173.59, out: 177.0,  lines: [[{ t: "ॐ", c: "y", big: true }, { t: "नमः", c: "w" }, { t: "शिवाय", c: "y" }]] },
  { start: 199.2,  out: 202.6,  lines: [[{ t: "राख", c: "r", big: true }], [{ t: "में", small: true }, { t: "बदल", c: "y" }, { t: "गया", c: "y" }]] },
  { start: 211.32, out: 214.7,  lines: [[{ t: "ज्योतिर्लिंग", c: "y" }]] },
  { start: 254.77, out: 258.2,  lines: [[{ t: "भस्म", c: "y" }, { t: "आरती", c: "w", big: true }]] },
  { start: 299.83, out: 303.0,  lines: [[{ t: "स्कंद", c: "y" }, { t: "पुराण", c: "w", big: true }]] },
  { start: 303.31, out: 306.7,  lines: [[{ t: "महाकालवन", c: "y" }]] },
  { start: 308.29, out: 311.7,  lines: [[{ t: "श्मशान", c: "r", big: true }]] },
  { start: 357.89, out: 361.3,  lines: [[{ t: "सुप्रीम", c: "y" }, { t: "कोर्ट", c: "w", big: true }]] },
  { start: 382.52, out: 385.9,  lines: [[{ t: "सिर्फ", c: "y" }, { t: "500", c: "w", big: true }], [{ t: "मिलीलीटर", c: "y" }]] },
  { start: 442.93, out: 446.3,  lines: [[{ t: "दक्षिणामुखी", c: "y" }]] },
  { start: 527.54, out: 531.0,  lines: [[{ t: "एक", c: "w" }, { t: "म्यान,", c: "y" }], [{ t: "दो", c: "w" }, { t: "तलवारें", c: "r", big: true }]] },
  { start: 553.49, out: 556.9,  lines: [[{ t: "सवारी", c: "y", big: true }]] },
  { start: 575.0,  out: 578.0,  lines: [[{ t: "विक्रमादित्य", c: "y" }]] },
  { start: 580.34, out: 583.7,  lines: [[{ t: "विक्रम", c: "y" }, { t: "संवत", c: "w", big: true }]] },
  { start: 599.3,  out: 602.7,  lines: [[{ t: "महाकवि", c: "y" }, { t: "कालिदास", c: "w", big: true }]] },
  { start: 624.64, out: 628.0,  lines: [[{ t: "राजा", c: "y" }, { t: "भर्तृहरि", c: "w", big: true }]] },
  { start: 702.27, out: 705.7,  lines: [[{ t: "नियम", c: "y" }, { t: "टूट", c: "r", big: true }, { t: "गया", c: "r" }]] },
  { start: 737.32, out: 740.5,  lines: [[{ t: "1235", c: "w", big: true }]] },
  { start: 741.54, out: 744.9,  lines: [[{ t: "सुल्तान", c: "y" }], [{ t: "इल्तुतमिश", c: "w" }]] },
  { start: 846.29, out: 849.7,  lines: [[{ t: "एपिग्राफिया", c: "y" }], [{ t: "इंडिका", c: "w", big: true }]] },
  { start: 888.58, out: 892.0,  lines: [[{ t: "शून्य", c: "y" }, { t: "रेखा", c: "w", big: true }]] },
  { start: 892.93, out: 896.3,  lines: [[{ t: "ग्रीनविच", c: "y" }, { t: "जैसा", c: "w" }]] },
  { start: 910.58, out: 914.0,  lines: [[{ t: "कर्क", c: "y" }, { t: "रेखा", c: "w", big: true }]] },
  { start: 953.42, out: 956.8,  lines: [[{ t: "काल", c: "y" }, { t: "के", small: true }, { t: "भी", small: true }], [{ t: "काल", c: "w", big: true }]] },
  { start: 990.41, out: 993.8,  lines: [[{ t: "महाकाल", c: "y" }, { t: "लोक", c: "w", big: true }], [{ t: "850", c: "w", big: true }, { t: "करोड़", c: "y" }]] },
  { start: 1016.09, out: 1019.5, lines: [[{ t: "सिंहस्थ", c: "y" }, { t: "कुंभ", c: "w", big: true }]] },
  { start: 1044.71, out: 1048.1, lines: [[{ t: "नागचंद्रेश्वर", c: "y" }]] },
  { start: 1060.84, out: 1064.2, lines: [[{ t: "साल", c: "y" }, { t: "में", small: true }, { t: "सिर्फ", c: "y" }], [{ t: "1", c: "w", big: true }, { t: "दिन", c: "w", big: true }]] },
  { start: 1113.09, out: 1116.5, lines: [[{ t: "कालभैरव", c: "y" }]] },
  { start: 1118.63, out: 1122.0, lines: [[{ t: "मदिरा", c: "r" }, { t: "का", small: true }, { t: "भोग", c: "w", big: true }]] },
  { start: 1254.04, out: 1257.4, lines: [[{ t: "मृत्युंजय", c: "y" }]] },
  { start: 1268.53, out: 1271.9, lines: [[{ t: "प्रलय", c: "r", big: true }], [{ t: "भी", small: true }, { t: "हार", c: "y" }, { t: "जाता", c: "y" }, { t: "है", c: "y" }]] },
  { start: 1298.31, out: 1301.7, lines: [[{ t: "समय", c: "y" }, { t: "के", small: true }, { t: "भी", small: true }], [{ t: "बाहर", c: "w", big: true }]] },
  { start: 1319.02, out: 1322.4, lines: [[{ t: "ओंकारेश्वर", c: "y" }]] },
];

const PhraseBlock: React.FC<{ p: Phrase; fps: number }> = ({ p, fps }) => {
  const frame = useCurrentFrame();
  const t = frame / fps;
  const phraseOp = interpolate(t, [p.out - 0.4, p.out], [1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  if (t < p.start - 0.1 || t > p.out + 0.1) return null;
  const gap = p.lineGap ?? LINE_GAP;
  const lh = p.lh ?? LH;
  let gi = -1;
  return (
    <div style={{ position: "absolute", top: PAD, left: PAD, fontFamily, opacity: phraseOp, display: "flex", flexDirection: "column" }}>
      {p.lines.map((line, li) => (
        <div key={li} style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: "0 16px", marginTop: li === 0 ? 0 : gap }}>
          {line.map((w, wi) => {
            gi += 1;
            const aT = p.start + gi * STAGGER;
            const k = Math.min(Math.max((t - aT) / DUR, 0), 1);
            const ease = 1 - Math.pow(1 - k, 3);
            const op = interpolate(t, [aT, aT + 0.18], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
            const y = -DROP * (1 - ease);
            return (
              <span key={wi} style={{
                display: "inline-block",
                transform: `translateY(${y}px)`,
                opacity: op,
                color: col(w.c),
                fontSize: w.big ? BIG_FS : w.small ? SMALL_FS : BASE_FS,
                fontWeight: w.big ? 900 : 800,
                lineHeight: lh,
                letterSpacing: p.ls ?? "0.005em",
                textShadow: SH,
              }}>{w.t}</span>
            );
          })}
        </div>
      ))}
    </div>
  );
};

// Bottom-RIGHT viewing tip — 2 lines (Kamakhya style), NO holes. Clears the आगे देखिए teaser (bottom-left).
const WatchTag: React.FC<{ fps: number; start: number; out: number }> = ({ fps, start, out }) => {
  const t = useCurrentFrame() / fps;
  if (t < start - 0.1 || t > out + 0.1) return null;
  const op = interpolate(t, [start, start + 0.4, out - 0.4, out], [0, 1, 1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <div style={{ position: "absolute", bottom: PAD + 6, right: PAD, textAlign: "right", fontFamily, opacity: op }}>
      <div style={{ color: YELLOW, fontSize: SMALL_FS, fontWeight: 800, letterSpacing: "0.01em", textShadow: SH }}>Watch at 1.2x</div>
      <div style={{ color: WHITE, fontSize: SMALL_FS * 0.7, fontWeight: 700, textShadow: SH }}>for best experience</div>
    </div>
  );
};

// Bottom-LEFT "आगे देखिए" teaser — yellow text + WHITE cycling dots, NO holes. Over the opening hook.
const AageDekhiye: React.FC<{ fps: number; start: number; out: number }> = ({ fps, start, out }) => {
  const t = useCurrentFrame() / fps;
  if (t < start - 0.1 || t > out + 0.1) return null;
  const op = interpolate(t, [start, start + 0.5, out - 0.6, out], [0, 1, 1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const dots = ["", ".", "..", "..."][Math.floor((t * 1.5) % 4)];
  return (
    <div style={{ position: "absolute", bottom: PAD + 6, left: PAD, fontFamily, opacity: op }}>
      <span style={{ color: YELLOW, fontSize: SMALL_FS, fontWeight: 800, letterSpacing: "0.01em", textShadow: SH }}>आगे देखिए</span>
      <span style={{ color: WHITE, fontSize: SMALL_FS, fontWeight: 800, textShadow: SH }}>{dots}</span>
    </div>
  );
};

// Master comp — holes-masked devotional phrases + non-masked UI (watch tip, teaser, LS pills).
export const MahakaleshwarOverlaysFull: React.FC<{ phrases?: Phrase[] }> = ({ phrases = MAHA_PHRASES }) => {
  const { fps } = useVideoConfig();
  const S = (s: number) => Math.round(s * fps);
  return (
    <AbsoluteFill>
      {/* holes distress mask over the dramatic top-left phrases only */}
      <AbsoluteFill style={{
        backgroundColor: "transparent",
        WebkitMaskImage: `url(${HOLES})`, maskImage: `url(${HOLES})`,
        WebkitMaskSize: "1280px 704px", maskSize: "1280px 704px",
        WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat",
        WebkitMaskPosition: "0 0", maskPosition: "0 0",
      }}>
        {phrases.map((p, i) => <PhraseBlock key={i} p={p} fps={fps} />)}
      </AbsoluteFill>

      {/* viewing tip (bottom-right, first ~5s) */}
      <WatchTag fps={fps} start={1.0} out={6.0} />

      {/* आगे देखिए teaser (bottom-left) over the opening hook 0:03 -> 1:52 */}
      <AageDekhiye fps={fps} start={3.0} out={112.0} />

      {/* Like+Subscribe — LS2 (bell) @ "सब्सक्राइब" 115.94s · LS5 (no bell) @ outro "सब्सक्राइब" 1310.42s */}
      <Sequence from={S(115.94)} durationInFrames={S(5.2)}><DevaKathaLS2 /></Sequence>
      <Sequence from={S(1310.42)} durationInFrames={S(5.2)}><DevaKathaLS5 /></Sequence>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
